import React, { useRef, useState } from 'react'
import styled from 'styled-components';
import LoadingOverlay from './LoadingOverlay';
import DateComponent from './DateComponent';
import { LinkStyle, MediumButton } from '../styles/Buttons';
import ExpandableReportTable from './ExpandableReportTable';
import { Color, Colors } from '../styles/Colors';
import Spinner from './Spinner';
import ScrollButton from './ScrollButton';
import { v4 as uuidv4 } from 'uuid';

const Container = styled.div`
  padding: 1.5rem 0 1.5rem 0; 
`;

const Error = styled.div`
  color: ${Color(Colors.red)}; 
  padding: 0.5rem 0 0 0;
`;

const ReportSummaryTable = styled.div`
  padding: 1rem 0 2rem 0; 
  & > div {
    display: flex; 

    & > p {
      flex: 1; 
    }

    & > p:not(:first-of-type) {
      text-align: right;
    }
  }
`;

const DateWrapper = styled.div`
  & > div {
    display: flex; 

    & > div {
      flex: 1; 
    }

    & > div:last-of-type {
      display: flex; 
      align-items: flex-end;
      flex-direction: column;
    }
  }

  & > div:first-of-type {
    padding-top: 1.5rem; 
  }

  & > div:nth-of-type(2) {
    padding: 1rem 0 1rem 0; 
  }

  & > div:last-of-type {
    padding-bottom: 1rem; 
  }
`;

const InputTypeNum = styled.input`
  margin: 0; 
`;

const GenerateReport = ({
  type,
  title,
  name,
  initialItems,
  dateStorageKeys,
  config,
  fetchReport,
  generateReport
}) => {
  const bottomRef = useRef(null); 

  const getTotalPages = (items = []) =>
    items.reduce((sum, item) => sum + (Number(item?.noofpages) || 0), 0);

  const [items, setItems] = useState(initialItems);

  const [dopDate, setDopDate] = useState(localStorage.getItem(dateStorageKeys.dop) || '');
  const [dowDateFrom, setDowDateFrom] = useState(localStorage.getItem(dateStorageKeys.dowFrom) || '');
  const [dowDateTo, setDowDateTo] = useState(localStorage.getItem(dateStorageKeys.dowTo) || '');

  const [year, setYear] = useState(localStorage.getItem(dateStorageKeys.year) || '');
  const [startMonth, setStartMonth] = useState(localStorage.getItem(dateStorageKeys.startMonth) || '');
  const [endMonth, setEndMonth] = useState(localStorage.getItem(dateStorageKeys.endMonth) || '');

  const [error, setError] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const dates = {
    ...(dateStorageKeys.dop && { dopDate }),
    ...(dateStorageKeys.dowFrom && { dowDateFrom }),
    ...(dateStorageKeys.dowTo && { dowDateTo }),
    ...(dateStorageKeys.year && { year }),
    ...(dateStorageKeys.startMonth && { startMonth }),
    ...(dateStorageKeys.endMonth && { endMonth }),
  };

  const handleGenerateReport = async () => {
    try {
      await generateReport(dates, items);
    } catch (error) {
      console.log(`Failed to generate ${name}`)
    }; 
  }; 


  let isLoadingMessage;

  if (type === 'quarterlyReport') {
    isLoadingMessage = `Hämtar förhandsgranskning av ${title} med årtal: ${year} och månadsspann: ${startMonth}-${endMonth}`
  };

  if (type === 'proposalReport' || type === 'publicationReport') {
    isLoadingMessage = `Hämtar förhandsgranskning av ${title} med faställelsedatum: ${dopDate}, upphävandedatum från: ${dowDateFrom} och upphävandedatum till: ${dowDateTo}`
  };

  const loading = {
    icon: Spinner,
    iconFill: Color(Colors.blue),
    title: 'Var god att vänta',
    subTitle: isLoadingMessage,
  }

  const handleGetReport = async () => {
    const newError = {};

    const usesDopDow =
      !!dateStorageKeys.dop ||
      !!dateStorageKeys.dowFrom ||
      !!dateStorageKeys.dowTo;

    const usesYearMonth =
      !!dateStorageKeys.year ||
      !!dateStorageKeys.startMonth ||
      !!dateStorageKeys.endMonth;

    if (usesDopDow) {
      if (!dopDate) newError.dopDate = 'Vänligen välj ett faställelsedatum'
      if (!dowDateTo) newError.dowDateTo = 'Vänligen välj ett upphävdandedatum (till)'
      if (!dowDateFrom) newError.dowDateFrom = 'Vänligen välj ett upphävdandedatum (från)'
    };

    if (usesYearMonth) {
      if (!year) newError.year = 'Vänligen välj ett årtal'
      if (!startMonth) newError.startMonth = 'Vänligen välj en start månad'
      if (!endMonth) newError.endMonth = 'Vänligen välj en slut måndad'
    };

    if (Object.keys(newError).length > 0) {
      setError(newError);
      setIsLoading(false);
      return;
    };

    setIsLoading(true);

    try {
      let reports;

      if (usesDopDow) {
        reports = await fetchReport({ dopDate, dowDateTo, dowDateFrom })
      }

      if (usesYearMonth) {
        reports = await fetchReport({ year, startMonth, endMonth })
      }
      
      const nextItem = {};

      config.forEach(({ key, apiKey }) => {
        nextItem[key] = (reports[apiKey] ?? []).map((item, index) => ({
          ...item, 
          id: uuidv4(),
        }))
      });

      setItems(nextItem);
      setError({});
    } catch (error) {
      console.log('Error', error);
    } finally {
      setIsLoading(false);
    }
  };

  const visibleItems = config
    .map((c) => ({
      ...c,
      data: items[c.key],
      pages: getTotalPages(items[c.key])
    }))
    .filter((item) => item.data.length > 0);

  return (
    <Container>
      {isLoading ? (
        <>
          {<LoadingOverlay {...loading} />}
        </>
      ) : (
        <>
          <h2>{name}</h2>
          <DateWrapper>
            {!!dateStorageKeys.dop && (
              <div>
                <div><h4>Faställelsedatum</h4></div>
                <div>
                  <DateComponent
                    usersDate={dopDate}
                    setUsersDate={setDopDate}
                    setError={setError}
                    tool='reportGenerator'
                    usersDateKey={dateStorageKeys.dop}
                  />
                  {Object.keys(error).length > 0 && (
                    <Error>
                      <p>{error.dopDate}</p>
                    </Error>
                  )}
                </div>
              </div>
            )}
            {!!dateStorageKeys.dowFrom && (
              <div>
                <div><h4>Upphävdandedatum från</h4></div>
                <div>
                  <DateComponent
                    usersDate={dowDateFrom}
                    setUsersDate={setDowDateFrom}
                    setError={setError}
                    tool='reportGenerator'
                    usersDateKey={dateStorageKeys.dowFrom}
                  />
                  {Object.keys(error).length > 0 && (
                    <Error>
                      <p>{error.dowDateFrom}</p>
                    </Error>
                  )}
                </div>
              </div>
            )}
            {!!dateStorageKeys.dowTo && (
              <div>
                <div><h4>Upphävdandedatum till</h4></div>
                <div>
                  <DateComponent
                    usersDate={dowDateTo}
                    setUsersDate={setDowDateTo}
                    setError={setError}
                    tool='reportGenerator'
                    usersDateKey={dateStorageKeys.dowTo}
                  />
                  {Object.keys(error).length > 0 && (
                    <Error>
                      <p>{error.dowDateTo}</p>
                    </Error>
                  )}
                </div>
              </div>
            )}
            {!!dateStorageKeys.year && (
              <div>
                <div><h4>År</h4></div>
                <div>
                  <InputTypeNum
                    type="number"
                    min="1900"
                    max="2900"
                    value={year}
                    onChange={(event) => {
                      setYear(event.target.value)
                      localStorage.setItem(dateStorageKeys.year, event.target.value)
                    }}
                    placeholder='Välj årtal'
                  />
                  {Object.keys(error).length > 0 && (
                    <Error>
                      <p>{error.year}</p>
                    </Error>
                  )}
                </div>
              </div>
            )}
            {!!dateStorageKeys.startMonth && (
              <div>
                <div><h4>Från månad</h4></div>
                <div>
                  <InputTypeNum
                    type="number"
                    min="1"
                    max="12"
                    value={startMonth}
                    onChange={(event) => {
                      setStartMonth(event.target.value)
                      localStorage.setItem(dateStorageKeys.startMonth, event.target.value)
                    }}
                    placeholder='Välj startmånad'
                  />
                  {Object.keys(error).length > 0 && (
                    <Error>
                      <p>{error.startMonth}</p>
                    </Error>
                  )}
                </div>
              </div>
            )}
            {!!dateStorageKeys.endMonth && (
              <div>
                <div><h4>Till månad</h4></div>
                <div>
                  <InputTypeNum
                    type="number"
                    min="1"
                    max="12"
                    value={endMonth}
                    onChange={(event) => {
                      setEndMonth(event.target.value)
                      localStorage.setItem(dateStorageKeys.endMonth, event.target.value)
                    }}
                    placeholder='Välj slutmånad'
                  />
                  {Object.keys(error).length > 0 && (
                    <Error>
                      <p>{error.endMonth}</p>
                    </Error>
                  )}
                </div>
              </div>
            )}
            <MediumButton onClick={handleGetReport} disabled={isLoading}>{`Hämta ${title}`}</MediumButton>
          </DateWrapper>
        </>
      )}
      {!isLoading && (
        <>
          {(visibleItems.length > 0) && (
            <>
              <ReportSummaryTable>
                <div>
                  <p></p>
                  <p>Antal standarder</p>
                  <p>Antal sidor</p>
                </div>
                {visibleItems.map(({ moduleId, title, data, pages }) => (
                  <div key={moduleId}>
                    <p><LinkStyle href={`#${moduleId}`}>{title}</LinkStyle></p>
                    <p>{data.length}</p>
                    <p>{pages ?? ''}</p>
                  </div>
                ))}
              </ReportSummaryTable>
              {visibleItems.map(({ index, moduleId, title, type, data, isEditable, key }) => (
                <ExpandableReportTable
                  key={moduleId}
                  title={title}
                  type={type}
                  data={data}
                  moduleId={moduleId}
                  isEditable={isEditable}
                  onChangeItem={(updatedItem) => {
                    setItems((prev) => ({
                      ...prev, 
                      [key]: prev[key].map((item) => 
                        item.id === updatedItem.id ? updatedItem : item
                      )
                    })); 
                  }}
                />
              ))}
              <MediumButton onClick={handleGenerateReport}>{`Generera ${title}`}</MediumButton>
              <ScrollButton bottomRef={bottomRef}/>
              <div ref={bottomRef}></div>
            </>
          )}
        </>
      )}
    </Container>
  )
}

export default GenerateReport
